// The Fleet page's top section as pure data (#6559, epic #6552): the four health tiles,
// the server insight rule and the Needs attention list. Presenters (`FleetHealthTiles`,
// `FleetAttention`) only render what this returns.
//
// EVERY JUDGEMENT IS THE SERVER'S. A machine's status, health and attention flag are its
// `verdict` (`machineVerdict`, `@developerz/domain`, #6557): "overloaded" is five one-minute
// samples decided server-side (owner ruling G8), never re-derived here from a raw CPU
// number. This file only counts, orders and groups those verdicts. The one reading it takes
// off the beat is `stats.hygiene.errors`, the clean-up failures, which the verdict folds into
// `needs_attention` but does not name.
//
// WAITING TO CONNECT IS NOT A MACHINE YET. A setup code minted and not yet used has nothing
// to check in or run, so it is outside every denominator here (Online, Busy right now, the
// insight's per-server total) and never needs attention (the verdict's neutral rule).
//
// The list follows the prototype's `attnList` (`docs/design/fleet-redesign/prototype.html`)
// exactly: (1) the highest-CPU overloaded machine; (2) the server insight; (3) every other
// issue, worst health first, then by CPU; (4) clean-up failing rows; (5) the remaining
// overloaded machines by CPU, which only ever appear behind "+N more". The DISPATCH row
// (#6708) sits directly after the insight: key slots stand free beside waiting work, the
// worker's alarm says so (`key_room.faulting`), and it is a fault of the platform's, not of
// a machine, so it carries none and counts toward no machine.
//
// A STUCK JOB (#6746) is the verdict's `jobs_state: 'stuck'`: a job silent for over an hour on
// a machine whose health may be fine. It flags `needs_attention` without a health to name, so
// it gets its own row, after the health issues and before the clean-up rows, and its own tile
// part, so it is never counted in "N machines" with no row saying why. Gated on the verdict's
// flag like every row: a neutral (drained, starting) machine with a stuck job gets none.

import type { MachineHealth } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import type { UtilBox, UtilKeyRoom, UtilLane, UtilQueue } from './fleetUtilization';

/** The rows Needs attention shows before "+N more". */
export const ATTENTION_SHOWN = 3;

/** A health that puts a machine on the list. `healthy` and `busy` are calm; `unknown` says nothing. */
export type IssueHealth = Exclude<MachineHealth, 'healthy' | 'busy' | 'unknown'>;

/** Worst first: the prototype's rank (offline / not responding 6 ... memory warning 3). */
const RANK: Readonly<Record<IssueHealth, number>> = {
  offline: 6,
  not_responding: 6,
  overloaded: 5,
  disk_critical: 4,
  memory_critical: 4,
  disk_warning: 3,
  memory_warning: 3,
};

/** Several machines on one server overloaded at once: the server is the bottleneck. */
export type ServerInsight = {
  readonly serverKey: string;
  /** What a person calls the server: its label, else its shared address. */
  readonly serverName: string;
  readonly overloaded: number;
  /** Machines on the server, waiting-to-connect excluded. */
  readonly total: number;
};

export type AttentionRow =
  | { readonly kind: 'issue'; readonly health: IssueHealth; readonly box: UtilBox }
  /** A job on the machine has made no progress for over an hour (#6746). */
  | { readonly kind: 'stuck_job'; readonly box: UtilBox }
  | { readonly kind: 'cleanup'; readonly box: UtilBox; readonly errors: number }
  | { readonly kind: 'insight'; readonly insight: ServerInsight }
  /** Key slots free beside waiting work (#6708): `keys` AI keys have room. No machine. */
  | { readonly kind: 'dispatch'; readonly keys: number };

export type AttentionList = {
  /** The first {@link ATTENTION_SHOWN} rows. */
  readonly shown: readonly AttentionRow[];
  /** Everything behind "+N more", in order. */
  readonly hidden: readonly AttentionRow[];
  /** Distinct machines needing attention: the section's and the tile's count. */
  readonly machines: number;
};

const isIssue = (h: MachineHealth): h is IssueHealth => h in RANK;

const counted = (b: UtilBox): boolean => b.verdict.status !== 'waiting_to_connect';

/** CPU demand now (load / processors), or -1 when the machine reported none: sorts last. */
const cpu = (b: UtilBox): number => b.verdict.signals.cpu.ratio ?? -1;

const cleanupErrors = (b: UtilBox): number => b.stats?.hygiene?.errors ?? 0;

const stuckJob = (b: UtilBox): boolean => b.verdict.jobs_state === 'stuck';

/** The rows that name a machine and open it ("View machine"); the rest name none. */
export function attentionBox(row: AttentionRow): UtilBox | null {
  return row.kind === 'issue' || row.kind === 'stuck_job' || row.kind === 'cleanup'
    ? row.box
    : null;
}

/**
 * The insight rule, the prototype's thresholds: on one server, at least 3 overloaded, or
 * at least 2 that are at least half of it. Most overloaded first.
 */
export function serverInsights(boxes: readonly UtilBox[]): ServerInsight[] {
  const groups = new Map<string, { name: string; overloaded: number; total: number }>();
  for (const b of boxes.filter(counted)) {
    const g = groups.get(b.server.key) ?? {
      name: b.server.label ?? b.server.address ?? b.display_name,
      overloaded: 0,
      total: 0,
    };
    g.total += 1;
    if (b.verdict.health === 'overloaded') g.overloaded += 1;
    groups.set(b.server.key, g);
  }
  return [...groups]
    .filter(([, g]) => g.overloaded >= 3 || (g.overloaded >= 2 && g.overloaded / g.total >= 0.5))
    .sort(([, a], [, b]) => b.overloaded - a.overloaded)
    .map(([serverKey, g]) => ({
      serverKey,
      serverName: g.name,
      overloaded: g.overloaded,
      total: g.total,
    }));
}

/**
 * The Needs attention list. `showInsight` is explicit: the page shows the insight row since
 * the Machines list can group by server (#6560, `FleetAttention`), and #6559's pins read the
 * list without it, where it takes no slot. `keyRoom` is the api's `key_room` (#6708): the
 * dispatch row shows iff it is faulting, whatever `showInsight` says; absent (an older api)
 * shows none.
 */
export function attentionList(
  boxes: readonly UtilBox[],
  opts: { readonly showInsight: boolean; readonly keyRoom?: UtilKeyRoom | null },
): AttentionList {
  // The verdict's own flag gates everything, clean-up rows included: it already leaves out
  // the neutral statuses (waiting to connect, paused, starting) whatever their beat says.
  const flagged = boxes.filter((b) => counted(b) && b.verdict.needs_attention);
  const issues = flagged
    .flatMap((b) => {
      const health = b.verdict.health;
      return isIssue(health) ? [{ kind: 'issue' as const, health, box: b }] : [];
    })
    .sort((a, b) => RANK[b.health] - RANK[a.health] || cpu(b.box) - cpu(a.box));
  const over = issues.filter((r) => r.health === 'overloaded');
  const insights: AttentionRow[] = opts.showInsight
    ? serverInsights(boxes).map((insight) => ({ kind: 'insight', insight }))
    : [];
  const stuck: AttentionRow[] = flagged
    .filter(stuckJob)
    .sort((a, b) => cpu(b) - cpu(a))
    .map((b) => ({ kind: 'stuck_job', box: b }));
  const cleanups: AttentionRow[] = flagged
    .filter((b) => cleanupErrors(b) > 0)
    .map((b) => ({ kind: 'cleanup', box: b, errors: cleanupErrors(b) }));
  const dispatch: AttentionRow[] = opts.keyRoom?.faulting
    ? [{ kind: 'dispatch', keys: opts.keyRoom.keys.length }]
    : [];
  const main: AttentionRow[] = [
    ...over.slice(0, 1),
    ...insights,
    ...dispatch,
    ...issues.filter((r) => r.health !== 'overloaded'),
    ...stuck,
    ...cleanups,
  ];
  return {
    shown: main.slice(0, ATTENTION_SHOWN),
    hidden: [...main.slice(ATTENTION_SHOWN), ...over.slice(1)],
    machines: flagged.length,
  };
}

/** What the line beside "+N more" names: up to three machines, then how many more. */
export type AttentionHint = {
  readonly names: readonly string[];
  readonly rest: number;
  /** Every deferred row is an overloaded machine: "are also overloaded." */
  readonly allOverloaded: boolean;
};

export function attentionHint(hidden: readonly AttentionRow[]): AttentionHint | null {
  // One entry per MACHINE (its id), so a machine with an issue and a clean-up row is named
  // once, and two machines that share a display name are still two.
  const byId = new Map<string, string>();
  // The insight and dispatch rows name no machine.
  for (const r of hidden) {
    const box = attentionBox(r);
    if (box !== null) byId.set(box.id, box.display_name);
  }
  const names = [...byId.values()];
  if (names.length === 0) return null;
  return {
    names: names.slice(0, ATTENTION_SHOWN),
    rest: Math.max(0, names.length - ATTENTION_SHOWN),
    allOverloaded: hidden.every((r) => r.kind === 'issue' && r.health === 'overloaded'),
  };
}

/** The hint sentence, through the caller's translator (the page's `t`, or a test's `en`). */
export function attentionHintText(hint: AttentionHint | null, tr: Translator): string {
  if (hint === null) return '';
  const [a = '', b = '', c = ''] = hint.names;
  const list =
    hint.rest > 0
      ? tr('fleet.top.attention.list_more', { a, b, c, count: hint.rest })
      : hint.names.length === 3
        ? tr('fleet.top.attention.list_three', { a, b, c })
        : hint.names.length === 2
          ? tr('fleet.top.attention.list_two', { a, b })
          : a;
  // The verb agrees with how many machines the list names, its "and N more" included.
  const key = hint.allOverloaded
    ? 'fleet.top.attention.hint_overloaded'
    : 'fleet.top.attention.hint_attention';
  return tr.plural(key, hint.names.length + hint.rest, { list });
}

/** One part of the Needs attention tile's sub-line, in the prototype's order. */
export type AttentionPartKind =
  | 'overloaded'
  | 'not_responding'
  | 'low_disk'
  | 'memory'
  | 'stuck_job'
  | 'cleanup';

const PART_OF: Readonly<Partial<Record<MachineHealth, AttentionPartKind>>> = {
  overloaded: 'overloaded',
  not_responding: 'not_responding',
  offline: 'not_responding',
  disk_critical: 'low_disk',
  disk_warning: 'low_disk',
  memory_critical: 'memory',
  memory_warning: 'memory',
};

const PART_ORDER: readonly AttentionPartKind[] = [
  'overloaded',
  'not_responding',
  'low_disk',
  'memory',
  'stuck_job',
  'cleanup',
];

export type FleetTiles = {
  /** Online "x of y": y leaves out machines waiting to connect. */
  readonly online: {
    readonly online: number;
    readonly total: number;
    /** Not responding or offline: silent for 90 seconds or more (owner ruling G14). */
    readonly notResponding: number;
    readonly waiting: number;
    /** The check-in store could not be read: neither online nor silent. */
    readonly unread: number;
  };
  /** Busy right now "x of y", the same denominator as Online. */
  readonly busy: {
    readonly working: number;
    readonly total: number;
    readonly idle: number;
    readonly paused: number;
    readonly notResponding: number;
    readonly starting: number;
  };
  /** Waiting in line: pending work summed over every lane. */
  readonly queued: number;
  /**
   * What is really holding that work (#6708), or null/absent when the api sent none: the
   * sub-line then falls back to today's copy (`queueLine`, `./fleet-queue-line.ts`).
   */
  readonly queue?: UtilQueue | null;
  readonly attention: {
    readonly machines: number;
    readonly parts: readonly { readonly kind: AttentionPartKind; readonly count: number }[];
  };
};

export function fleetTiles(
  boxes: readonly UtilBox[],
  lanes: readonly UtilLane[],
  queue: UtilQueue | null = null,
): FleetTiles {
  const machines = boxes.filter(counted);
  const by = (...statuses: readonly string[]): number =>
    machines.filter((b) => statuses.includes(b.verdict.status)).length;
  const unread = by('unknown');
  // Online is a live check-in (`online`, the presence key), never a status word: a drained
  // machine reads `paused` in the domain verdict whether or not it still checks in (drain
  // wins `machineStatus`'s precedence), so a silent paused machine is counted silent here.
  const notResponding =
    by('not_responding', 'offline') +
    machines.filter((b) => b.verdict.status === 'paused' && !b.online).length;
  const starting = by('starting');
  const flagged = machines.filter((b) => b.verdict.needs_attention);
  const counts = new Map<AttentionPartKind, number>();
  const bump = (k: AttentionPartKind): void => {
    counts.set(k, (counts.get(k) ?? 0) + 1);
  };
  for (const b of flagged) {
    const part = PART_OF[b.verdict.health];
    if (part !== undefined) bump(part);
    if (stuckJob(b)) bump('stuck_job');
    if (cleanupErrors(b) > 0) bump('cleanup');
  }
  return {
    online: {
      online: machines.length - notResponding - unread - starting,
      total: machines.length,
      notResponding,
      waiting: boxes.length - machines.length,
      unread,
    },
    busy: {
      working: by('working'),
      total: machines.length,
      idle: by('idle'),
      paused: by('paused'),
      notResponding,
      starting,
    },
    queued: lanes.reduce((sum, l) => sum + l.depth, 0),
    queue,
    attention: {
      machines: flagged.length,
      parts: PART_ORDER.flatMap((kind) => {
        const count = counts.get(kind) ?? 0;
        return count > 0 ? [{ kind, count }] : [];
      }),
    },
  };
}
